import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Image, ProgressBar, Row, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styling/profile.css";

import { s3Upload, __s3FileStreamDownload } from "../helper_classes/AWSS3";
import { postJson } from "../helper_classes/http";
import { Buffer } from "buffer";

import individual from "../icons/individual.png";
import company from "../icons/enterprise.png";
import unauthorized from "../icons/unauthorized.png";

import { loggedinUsersEmail as loggedin_user_email } from "../App.js";


const ACCOUNT_STATUS_INITIAL = {
    email: "",
    name: "",
    address: "",
    phone_number: "",
    secondary_email: "",
    pricing_package: "",
    billing_cycle: "",
    package_cost: "",
    expiry_date: "",
    max_view_allowed: "",
    max_uploads_allowed: "",
    max_storage_space: "",
    max_file_size: "",
    download_counter: "",
    upload_counter: "",
    storage_space_used: "",
    subscriptionDate: "",
    lastActive: "",
    account_type: "",
    image_icon_uploaded: ""
};

const PROFILE_PICTURE_KEYS = [
    "profilePicture/profile-picture",
    "profilePicture/profile-picture.jpg",
    "profilePicture/profile-picture.jpeg",
    "profilePicture/profile-picture.png",
    "profilePicture/profile-picture.webp"
];

const ALLOWED_PROFILE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const DEFAULT_PROFILE_MIME_TYPE = "image/jpeg";

const toNumber = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toPercent = (value, limit) => {
    const numericLimit = toNumber(limit);

    if (numericLimit <= 0) {
        return 0;
    }

    const percentage = Math.round((toNumber(value) / numericLimit) * 100);
    return clamp(percentage, 0, 100);
};

const getUsageVariant = (percentage) => {
    if (percentage >= 90) {
        return "danger";
    }

    if (percentage >= 70) {
        return "warning";
    }

    return "success";
};

const inferMimeTypeFromKey = (key) => {
    if (key.endsWith(".png")) {
        return "image/png";
    }

    if (key.endsWith(".webp")) {
        return "image/webp";
    }

    return "image/jpeg";
};

const unixTimeToDateTime = (unixTime) => {
    const numericTimestamp = toNumber(unixTime);

    if (!numericTimestamp) {
        return "-";
    }

    const normalizedTimestamp = numericTimestamp < 1000000000000
        ? numericTimestamp * 1000
        : numericTimestamp;

    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    }).format(normalizedTimestamp);
};


const UserProfile = () => {
    const [profilePictureBase64, setProfilePictureBase64] = useState(null);
    const [profilePictureMimeType, setProfilePictureMimeType] = useState(DEFAULT_PROFILE_MIME_TYPE);
    const [accountStatus, setAccountStatus] = useState(ACCOUNT_STATUS_INITIAL);

    const [uploadButtonDisabled, setUploadButtonDisabled] = useState(false);
    const [userAccountStatusLoaded, setUserAccountStatusLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [uploadError, setUploadError] = useState("");
    const [uploadSuccess, setUploadSuccess] = useState("");

    const activeUserEmail = (loggedin_user_email || "").trim();

    const loadProfilePicture = useCallback(async() => {
        for (const key of PROFILE_PICTURE_KEYS) {
            try {
                const dataStream = await __s3FileStreamDownload({
                    bucketName: "xenkiaassetbucket",
                    subPath: key
                });

                const body = dataStream?.data?.Body;

                if (body) {
                    const contentType = dataStream?.data?.ContentType || inferMimeTypeFromKey(key);

                    setProfilePictureBase64(Buffer.from(body).toString("base64"));
                    setProfilePictureMimeType(contentType);

                    return true;
                }
            }
            catch (error) {
                // Continue trying other possible file names.
            }
        }

        setProfilePictureBase64(null);
        setProfilePictureMimeType(DEFAULT_PROFILE_MIME_TYPE);
        return false;
    }, []);

    const getUserAccountStatus = useCallback(async() => {
        setIsLoading(true);
        setLoadError("");

        if (!activeUserEmail) {
            setUserAccountStatusLoaded(false);
            setIsLoading(false);
            setLoadError("Could not determine your account email. Please sign out and sign in again.");
            return;
        }

        try {
            const response = await postJson("https://oidnlpynvl.execute-api.us-east-1.amazonaws.com/prod/accountstatus", {
                email: activeUserEmail
            });

            if (Number(response?.statusCode) !== 201) {
                throw new Error(`Unexpected account status response: ${response?.statusCode}`);
            }

            const accountItem = response?.body?.response?.Item;

            if (!accountItem) {
                throw new Error("Account status payload is missing.");
            }

            setAccountStatus({
                email: accountItem.email || "",
                name: accountItem.name || "",
                address: accountItem.address || "",
                phone_number: accountItem.phone_number || "",
                secondary_email: accountItem.secondary_email || "",
                pricing_package: accountItem.pricing_package || "",
                billing_cycle: accountItem.billing_cycle || "",
                package_cost: accountItem.package_cost || "",
                expiry_date: accountItem.expiry_date || "",
                max_view_allowed: accountItem.max_view_allowed || "",
                max_uploads_allowed: accountItem.max_uploads_allowed || "",
                max_storage_space: accountItem.max_storage_space || "",
                max_file_size: accountItem.max_file_size || "",
                download_counter: accountItem.download_counter || "",
                upload_counter: accountItem.upload_counter || "",
                storage_space_used: accountItem.storage_space_used || "",
                subscriptionDate: accountItem.subscriptionDate || "",
                lastActive: accountItem.lastActive || "",
                account_type: accountItem.account_type || "",
                image_icon_uploaded: accountItem.image_icon_uploaded || false
            });

            if (accountItem.image_icon_uploaded === true) {
                await loadProfilePicture();
            }
            else {
                setProfilePictureBase64(null);
                setProfilePictureMimeType(DEFAULT_PROFILE_MIME_TYPE);
            }

            setUserAccountStatusLoaded(true);
        }
        catch (error) {
            console.error("Failed to load account status.", error);
            setUserAccountStatusLoaded(false);
            setLoadError("Could not load profile information. Please try again.");
        }
        finally {
            setIsLoading(false);
        }
    }, [activeUserEmail, loadProfilePicture]);

    useEffect(() => {
        getUserAccountStatus();
    }, [getUserAccountStatus]);


    const updateProfilePictureUploadedDB = async() => {
        if (!activeUserEmail) {
            throw new Error("Missing account email for profile picture update.");
        }

        const response = await postJson("https://oidnlpynvl.execute-api.us-east-1.amazonaws.com/prod/increaseviewcount", {
            email: activeUserEmail,
            field_to_update: "image_icon_uploaded",
            value: true,
            inc_dec_reset: "update"
        });

        if (Number(response?.statusCode) !== 201) {
            throw new Error(`Failed to set profile picture flag. Response: ${response?.statusCode}`);
        }
    };


    const selectProfilePicture = async(event) => {
        const file = event?.target?.files?.[0];

        if (!file) {
            return;
        }

        event.target.value = "";
        setUploadError("");
        setUploadSuccess("");

        if (!ALLOWED_PROFILE_IMAGE_TYPES.has(file.type)) {
            setUploadError("Unsupported file format. Please upload JPG, PNG, or WEBP images.");
            return;
        }

        setUploadButtonDisabled(true);

        try {
            await s3Upload({
                bucketName: "xenkiaassetbucket",
                object: file,
                subPath: "profilePicture/profile-picture",
                contentType: file.type,
                prog: () => {}
            });

            await updateProfilePictureUploadedDB();
            await loadProfilePicture();

            setAccountStatus((previousState) => ({
                ...previousState,
                image_icon_uploaded: true
            }));

            setUploadSuccess("Profile picture uploaded successfully.");
        }
        catch (error) {
            console.error("Failed to upload profile picture.", error);
            setUploadError("Profile picture upload failed. Please try again.");
        }
        finally {
            setUploadButtonDisabled(false);
        }
    };


    const uploadCounter = toNumber(accountStatus.upload_counter);
    const uploadLimit = toNumber(accountStatus.max_uploads_allowed);
    const uploadPercentage = toPercent(uploadCounter, uploadLimit);

    const downloadCounter = toNumber(accountStatus.download_counter);
    const downloadLimit = toNumber(accountStatus.max_view_allowed);
    const downloadPercentage = toPercent(downloadCounter, downloadLimit);

    const storageUsedMb = toNumber(accountStatus.storage_space_used);
    const storageUsedGb = storageUsedMb / 1024;
    const storageLimitGb = toNumber(accountStatus.max_storage_space);
    const storagePercentage = toPercent(storageUsedGb, storageLimitGb);

    const usageMetrics = [
        {
            key: "uploads",
            title: "Upload Counter",
            percentage: uploadPercentage,
            details: `${uploadCounter} / ${uploadLimit} per month`
        },
        {
            key: "downloads",
            title: "Download/View Counter",
            percentage: downloadPercentage,
            details: `${downloadCounter} / ${downloadLimit} per month`
        },
        {
            key: "storage",
            title: "Storage Used",
            percentage: storagePercentage,
            details: storageUsedMb > 1024
                ? `${Math.round(storageUsedGb)} GB / ${storageLimitGb} GB`
                : `${storageUsedMb} MB / ${storageLimitGb} GB`
        }
    ];

    const basicInfoRows = [
        { key: "name", label: "Name", value: accountStatus.name || "-" },
        { key: "email", label: "Email", value: accountStatus.email || "-" },
        { key: "address", label: "Address", value: accountStatus.address || "-" },
        { key: "phone", label: "Phone number", value: accountStatus.phone_number || "-" }
    ];

    const packageInfoRows = [
        { key: "accountType", label: "Account type", value: accountStatus.account_type || "-" },
        { key: "package", label: "Package", value: accountStatus.pricing_package || "-" },
        { key: "bill", label: "Last bill amount", value: `$${accountStatus.package_cost || "0"} USD` },
        { key: "cycle", label: "Billing cycle", value: accountStatus.billing_cycle || "-" },
        { key: "registered", label: "Registration date", value: unixTimeToDateTime(accountStatus.subscriptionDate) },
        { key: "expiry", label: "Subscription expiry date", value: unixTimeToDateTime(accountStatus.expiry_date) },
        { key: "maxSize", label: "Max file size allowed", value: `${accountStatus.max_file_size || "0"} MB` }
    ];

    return (
        <div className="profile-wrapper">
            <Card className="profile-hero-card">
                <div className="profile-avatar-wrap">
                    {accountStatus.image_icon_uploaded && profilePictureBase64
                        ? (
                            <Image
                                src={`data:${profilePictureMimeType};base64,${profilePictureBase64}`}
                                alt="profile-picture"
                                className="profile-avatar"
                                roundedCircle
                            />
                        )
                        : (accountStatus.account_type === "Individual")
                            ? <Image src={individual} alt="individual-account-icon" className="profile-avatar" roundedCircle />
                            : <Image src={company} alt="company-account-icon" className="profile-avatar" roundedCircle />
                    }
                </div>

                <label className={`profile-file-input ${uploadButtonDisabled ? "disabled" : ""}`}>
                    <span>{uploadButtonDisabled ? "Uploading..." : "Upload profile picture"}</span>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={selectProfilePicture}
                        disabled={uploadButtonDisabled}
                    />
                </label>
                <p className="profile-file-help">Supported formats: JPG, PNG, WEBP</p>

                {uploadSuccess && <Alert variant="success" className="profile-alert">{uploadSuccess}</Alert>}
                {uploadError && <Alert variant="danger" className="profile-alert">{uploadError}</Alert>}
            </Card>

            {isLoading
                ? (
                    <div className="profile-loading">
                        <Spinner animation="border" variant="success" />
                    </div>
                )
                : loadError
                    ? (
                        <Card className="profile-error-card">
                            <Alert variant="danger" className="profile-alert-inline">{loadError}</Alert>
                            <Button variant="outline-primary" onClick={getUserAccountStatus}>Retry</Button>
                        </Card>
                    )
                    : userAccountStatusLoaded
                        ? (
                            <>
                                <Card className="profile-section-card">
                                    <h2 className="profile-section-title">Basic user information</h2>
                                    <Container fluid>
                                        {basicInfoRows.map((row) => (
                                            <Row className="profile-info-row" key={row.key}>
                                                <Col xs={12} md={4}>
                                                    <p className="upload-subtext profile-label profile-info-label"><b>{row.label}:</b></p>
                                                </Col>
                                                <Col xs={12} md={8}>
                                                    <p className="upload-subtext profile-info-value">{row.value}</p>
                                                </Col>
                                            </Row>
                                        ))}
                                    </Container>
                                </Card>

                                <Card className="profile-section-card">
                                    <h2 className="profile-section-title">Package</h2>
                                    <Container fluid>
                                        {packageInfoRows.map((row) => (
                                            <Row className="profile-info-row" key={row.key}>
                                                <Col xs={12} md={4}>
                                                    <p className="upload-subtext profile-label profile-info-label"><b>{row.label}:</b></p>
                                                </Col>
                                                <Col xs={12} md={8}>
                                                    <p className="upload-subtext profile-info-value">{row.value}</p>
                                                </Col>
                                            </Row>
                                        ))}
                                    </Container>
                                </Card>

                                <Card className="profile-section-card">
                                    <h2 className="profile-section-title">Usage status</h2>

                                    <div className="profile-usage-list">
                                        {usageMetrics.map((metric) => (
                                            <div className="profile-usage-row" key={metric.key}>
                                                <div className="profile-usage-heading">
                                                    <span className="profile-usage-title">{metric.title}</span>

                                                    <div className="profile-usage-meta">
                                                        <span className="profile-usage-count">{metric.details}</span>
                                                        <span className="profile-usage-percent">{metric.percentage}%</span>
                                                    </div>
                                                </div>

                                                <ProgressBar
                                                    className="profile-progress"
                                                    now={metric.percentage}
                                                    variant={getUsageVariant(metric.percentage)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </>
                        )
                        : null}
        </div>
    );
};


function Profile(props) {
    if (props.loggedIn === true) {
        return (
            <header className="upload-header profile-page-root">
                <UserProfile />
            </header>
        );
    }

    return (
        <div className="profile-unauthorized">
            <div>
                <img src={unauthorized} alt="unauthorized" height="96" />
                <hr />
                <h3>Please login to access your profile</h3>
            </div>
        </div>
    );
}


export default Profile;
